import { Extension, type Editor, type Range } from "@tiptap/core";
import Suggestion, { type SuggestionOptions } from "@tiptap/suggestion";
import { DID_SELECTION_KEY } from "../constants";
import {
  requestEditorOpen,
  type DynamicNodeKind,
  type SlashDataKeyItem,
} from "./data-key-registry";
import { renderSlashCommandPopup } from "./slash-command-popup";

const SLASH_COMMAND_PLUGIN_NAME = "dynamicNodeSlashCommand";

/**
 * Insertable dynamic-node commands offered by the `/` menu. Each one inserts an
 * empty node; the node's own editor bubble then collects its details (the data
 * key, the context variable, etc.). Conditional nodes will be added here later.
 */
const SLASH_COMMANDS: SlashDataKeyItem[] = [
  { key: "datakey", label: "Data Key" },
  { key: "context", label: "Context" },
  { key: "did", label: "Document Reference" },
  { key: "conditional", label: "Conditional (If/Else)" },
];

export const filterCommands = (query: string): SlashDataKeyItem[] => {
  const normalized = query.trim().toLowerCase();
  if (!normalized) {
    return SLASH_COMMANDS;
  }
  return SLASH_COMMANDS.filter(
    (item) =>
      item.key.toLowerCase().includes(normalized) ||
      item.label?.toLowerCase().includes(normalized)
  );
};

export const insertDynamicNodeFromSlashCommand = (
  editor: Editor,
  range: Range,
  kind: DynamicNodeKind
): void => {
  requestEditorOpen(kind);
  const chain = editor.chain().focus().deleteRange(range);
  if (kind === "datakey") {
    chain.insertDataKey("");
  } else if (kind === "context") {
    chain.insertContextKey("");
  } else if (kind === "did") {
    chain.insertDid(DID_SELECTION_KEY);
  } else {
    chain.insertConditional("");
  }
  chain.setTextSelection(range.from + 1).run();
};

/**
 * `/` slash command that inserts a dynamic node. Built on Tiptap's `Suggestion`
 * utility: typing `/` opens a menu of node types; picking "Data Key" removes the
 * `/query` text, inserts an EMPTY `dataKey` node, and raises a one-shot signal so
 * the new node opens its picker bubble (where the user chooses the key).
 */
export const SlashCommand = Extension.create({
  name: SLASH_COMMAND_PLUGIN_NAME,

  addProseMirrorPlugins() {
    const suggestion: Omit<
      SuggestionOptions<SlashDataKeyItem, SlashDataKeyItem>,
      "editor"
    > = {
      char: "/",
      startOfLine: false,
      allowSpaces: false,
      items: ({ query }) => filterCommands(query),
      command: ({ editor, range, props }) => {
        // Raise the one-shot "open my picker" flag BEFORE inserting, so it's set
        // regardless of whether the React node view mounts synchronously during
        // `run()` or on a later microtask. The empty node is then inserted and
        // the text cursor is placed after it; its view consumes the flag on mount
        // and opens the picker bubble without activating the selection toolbar.
        if (
          props.key === "datakey" ||
          props.key === "context" ||
          props.key === "did" ||
          props.key === "conditional"
        ) {
          insertDynamicNodeFromSlashCommand(editor, range, props.key);
        }
      },
      render: renderSlashCommandPopup,
    };

    return [
      Suggestion<SlashDataKeyItem, SlashDataKeyItem>({
        editor: this.editor,
        ...suggestion,
      }),
    ];
  },
});

export default SlashCommand;
